import { filter, find, findIndex, flatMap, get, some, uniq } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useTranslate } from '../../../../../core/translate';
import { TRIMS } from '../../../../common/steps';
import useLargeBreakpoint from '../../../../hooks/useLargeBreakpoint';
import MoreViewsTwoD from '../../../../main/moreViews/MoreViewsTwoD';
import GridView from '../../../../views/gridView/GridView';
import { findClosestMsrp } from '../../findClosestMsrp';
import TrimFeaturesModal from '../features/trimFeaturesModal/TrimFeaturesModal';
import {
  getTrimGroupWDTSelection,
  getTrimsWithSelectedEngine,
  getTrimsWithSelectedFeatures,
} from '../filters/filterHelpers';
import PopConFilter from '../filters/popConFilter/PopConFilter';
import TrimCard from '../TrimCard';
import TrimCompareModal from '../trimCompareModal/TrimCompareModal';
import {
  saveTrimFilters,
  getTrimFilters,
} from '../../../../resources/localStorage/localStorage.service';
import TrimWithoutFiltering from '../TrimWithoutFiltering/TrimWithoutFiltering';
import { normalizeDriveAndWheelTypes } from '../utils';
import AlignCardsContent from '../../../../common/AlignCardsContent';
import {
  dispatchCustomEvent,
  BYO_DRIVE_TYPE_CHANGE,
} from '../../../../utils/customMessage';

const TrimWithFiltering = (props) => {
  const isAtLeastLarge = useLargeBreakpoint();

  const {
    config,
    disclaimer,
    appResources,
    breakpoint,
    disableCompare,
    isSelectionRevealed,
    onChange,
    isUpdating,
    features,
    updatingOption,
    postalCode,
    modelInfo,
    bottomOffset,
    setBottomOffset,
    animation,
    trimMatrix,
    trims,
  } = props;

  const { selections, msrp } = config;
  const { bodyTypes, engines } = trimMatrix;

  const keyFeatures = find(bodyTypes, { id: selections.bodyTypeId })
    ?.keyFeatures;

  const totalMsrpWithOptions = get(msrp, 'totalMsrpWithOptions');
  const translate = useTranslate();
  const navigate = useNavigate();

  const onClose = () => {
    navigate('');
  };

  const hasCabSizes = some(bodyTypes, 'cabSize');

  const filteredBodyType =
    bodyTypes &&
    bodyTypes.filter((bodyType) =>
      hasCabSizes
        ? bodyType.cabSize === selections.cabSize
        : bodyType.id === selections.bodyTypeId
    );

  const driveAndWheelTypes = flatMap(filteredBodyType, 'driveTypes');

  const uniqueDriveAndWheelTypes = normalizeDriveAndWheelTypes(
    driveAndWheelTypes
  );

  const normalizeTrim = (trimNameWithRWT) => {
    const rearWheelOptions = uniq(
      uniqueDriveAndWheelTypes.map(({ rearWheelType }) => rearWheelType)
    );

    return rearWheelOptions
      .reduce((accum, opt) => {
        if (accum) {
          return accum.replace(opt, '');
        }
        return trimNameWithRWT.replace(opt, '');
      }, '')
      .trim();
  };

  const getFilteredData = (
    cabSize,
    driveType,
    rearWheelType,
    engineCode = null
  ) => {
    const filteredTrims = filter(trims, (trim) =>
      hasCabSizes
        ? trim.cabSize === cabSize
        : trim.bodyTypeId === selections.bodyTypeId
    );

    const options = flatMap(filteredTrims, 'options');
    const msrp = get(config, 'msrp.totalMsrpWithOptions');
    let opts = { driveType, rearWheelType };
    if (engineCode) {
      opts = { ...opts, engineCode };
    }

    let filteredOptions = filter(options, opts);

    if (rearWheelType) {
      filteredOptions = filteredOptions.map((filtered) => {
        return {
          ...filtered,
          abbreviatedTrim: normalizeTrim(filtered.trimName),
        };
      });
    }

    return { msrp, filteredOptions };
  };

  const updateFeatureSelections = (filteredOptions, newFilterSelections) => {
    let compatibleFeatureSelections = newFilterSelections.selectedKeyFeatures;

    let tempOptions = null;

    compatibleFeatureSelections = compatibleFeatureSelections.filter(
      (featureCode) => {
        const keyFeature = find(keyFeatures, { code: featureCode });
        let availableOpts = tempOptions || filteredOptions;
        availableOpts = availableOpts.filter((option) => {
          return keyFeature?.styleIds.includes(option.styleId);
        });

        if (availableOpts.length > 0) {
          tempOptions = availableOpts;
          return true;
        }
      }
    );

    return {
      ...newFilterSelections,
      selectedKeyFeatures: compatibleFeatureSelections,
    };
  };

  const [filterSelections, setFilterSelections] = useState(
    getTrimFilters(modelInfo, selections.bodyTypeId) || {
      driveType: '',
      rearWheelType: '',
      engine: '',
      selectedKeyFeatures: [],
      hasSelectedFilter: false,
    }
  );

  useEffect(() => {
    if (selections) {
      setFilterSelections({
        ...filterSelections,
        driveType: selections.driveType,
        rearWheelType: selections.rearWheelType,
      });
    }
  }, [selections]);

  useEffect(() => {
    saveTrimFilters(modelInfo, selections.bodyTypeId, filterSelections);
    dispatchCustomEvent(BYO_DRIVE_TYPE_CHANGE);
  }, [filterSelections]);

  const trimsWithSelectedBodyType = filter(trims, (x) =>
    hasCabSizes
      ? x.rearWheelType === filterSelections.rearWheelType &&
        x.cabSize === selections.cabSize
      : x.bodyTypeId === config.bodyType.id
  );

  const allPossibleTrims = filter(trims, (x) =>
    hasCabSizes
      ? x.cabSize === selections.cabSize
      : x.bodyTypeId === config.bodyType.id
  );
  const numberOfCards = allPossibleTrims.length;

  const getClosestMatchForDriveType = (
    rearWheelType,
    { trimName, engine, transmission },
    filteredOptions
  ) => {
    const abbrevOrFullTrim = rearWheelType
      ? { abbreviatedTrim: normalizeTrim(trimName) }
      : { trimName };
    return (
      find(filteredOptions, {
        ...abbrevOrFullTrim,
        engineCode: engine,
        transmissionCode: transmission,
      }) ||
      find(filteredOptions, {
        ...abbrevOrFullTrim,
        engineCode: engine,
      }) ||
      findClosestMsrp(
        filter(filteredOptions, {
          ...abbrevOrFullTrim,
        })
      ) ||
      findClosestMsrp(filteredOptions, msrp)
    );
  };

  const getClosestMatchForEngine = (
    engine,
    { driveType, rearWheelType, cabSize, trimName, transmission }
  ) => {
    const { msrp, filteredOptions } = getFilteredData(
      cabSize,
      driveType,
      rearWheelType
    );

    return (
      find(filteredOptions, {
        trimName,
        engineCode: engine,
        transmissionCode: transmission,
      }) ||
      find(filteredOptions, {
        engineCode: engine,
        transmissionCode: transmission,
      }) ||
      findClosestMsrp(filter(filteredOptions, { engine })) ||
      findClosestMsrp(filteredOptions, msrp)
    );
  };

  const onFeatureChange = (feature) => {
    const { selectedKeyFeatures } = filterSelections;

    if (selectedKeyFeatures.includes(feature.code)) {
      setFilterSelections({
        ...filterSelections,
        selectedKeyFeatures: selectedKeyFeatures.filter(
          (rpo) => rpo !== feature.code
        ),
        hasSelectedFilter: true,
      });
    } else {
      setFilterSelections({
        ...filterSelections,
        selectedKeyFeatures: [...selectedKeyFeatures, feature.code],
        hasSelectedFilter: true,
      });
    }
  };

  const getTrimGroups = (filter) => {
    let filteredTrimList = trimsWithSelectedBodyType;
    let selectionGroup = filterSelections;

    if (filter) {
      selectionGroup = filterSelections.driveType
        ? { ...filterSelections, cabSize: selections.cabSize }
        : selections;
      filteredTrimList = getTrimsWithSelectedEngine(
        trimsWithSelectedBodyType,
        selectionGroup
      );

      if (filterSelections.selectedKeyFeatures.length > 0) {
        filteredTrimList = getTrimsWithSelectedFeatures(
          filteredTrimList,
          selectionGroup,
          keyFeatures
        );
      }
    }

    return getTrimGroupWDTSelection(filteredTrimList, selectionGroup);
  };

  const trimGroups = getTrimGroups(true);

  const trimGroupsFilteredByDT = getTrimGroups(false);

  const getDefaultDriveObj = () => {
    const bodyDefault = bodyTypes.filter((bodyType) =>
      hasCabSizes
        ? bodyType.cabSize === selections.cabSize
        : bodyType.id === selections.bodyTypeId
    )[0];
    const { rearWheelType, driveType } = bodyDefault.driveTypes.find(
      (x) => x.default
    );
    return find(uniqueDriveAndWheelTypes, {
      rearWheelType: rearWheelType || '',
      driveType,
    });
  };

  const isSelectionSupportedByProposedFilter = (filteredOptions, engine) => {
    let selectionOptions = {
      ...(config.selections.rearWheelType
        ? { abbreviatedTrim: normalizeTrim(config.selections.trimName) }
        : { trimName: config.selections.trimName }),
    };

    if (engine) {
      selectionOptions = { ...selectionOptions, engineCode: engine };
    }
    return some(filteredOptions, selectionOptions);
  };

  const isEngineSupportedByProposedFilter = (filteredOptions, engine) => {
    return engine
      ? some(filteredOptions, {
          engineCode: engine,
        })
      : true;
  };

  const calcDriveChangeById = (driveId, popConFilterSelections) => {
    let driveTypeValue = find(uniqueDriveAndWheelTypes, { id: driveId });
    const { driveType, rearWheelType } = driveTypeValue;
    const { engine } = popConFilterSelections;
    const { filteredOptions } = getFilteredData(
      selections.cabSize,
      driveType,
      rearWheelType
    );

    let newFilterSelections = {
      ...popConFilterSelections,
      driveType,
      rearWheelType,
    };

    const isEngineInFilter = isEngineSupportedByProposedFilter(
      filteredOptions,
      engine
    );

    const isCurrentSelectionInFilter = isSelectionSupportedByProposedFilter(
      filteredOptions,
      selections.engine
    );

    if (!isEngineInFilter) {
      newFilterSelections = { ...newFilterSelections, engine: '' };
    }

    if (popConFilterSelections?.selectedKeyFeatures.length > 0) {
      newFilterSelections = updateFeatureSelections(
        filteredOptions,
        newFilterSelections
      );
    }

    setFilterSelections(newFilterSelections);

    if (isSelectionRevealed) {
      if (!isCurrentSelectionInFilter) {
        return;
      }
    }

    const option = getClosestMatchForDriveType(
      rearWheelType,
      config.selections,
      filteredOptions
    );

    if (
      option.engineCode !== selections.engine ||
      option.styleId !== selections.styleId ||
      option.transmissionCode !== selections.transmission ||
      option.rearWheelType !== selections.rearWheelType
    ) {
      onChange(option, true);
    }
  };

  const onDriveChange = (e) => {
    const newFilterSelections = {
      ...filterSelections,
      hasSelectedFilter: true,
    };
    calcDriveChangeById(e.target.value, newFilterSelections);
  };

  const onEngineChange = (engineCode) => {
    if (filterSelections.engine !== engineCode) {
      let newFilterSelections = {
        ...filterSelections,
        engine: engineCode,
        hasSelectedFilter: true,
      };

      const { filteredOptions } = getFilteredData(
        selections.cabSize,
        filterSelections.driveType,
        filterSelections.rearWheelType,
        engineCode
      );

      // TODO: MOVE TO REUSABLE FUNCTION.
      if (filterSelections?.selectedKeyFeatures.length > 0) {
        newFilterSelections = updateFeatureSelections(
          filteredOptions,
          newFilterSelections
        );
      }

      setFilterSelections(newFilterSelections);

      if (engineCode) {
        if (isSelectionRevealed && selections.engineCode !== engineCode) {
          return;
        }

        if (!isSelectionRevealed) {
          const option = getClosestMatchForEngine(engineCode, {
            ...selections,
            newFilterSelections,
          });
          onChange(option, true);
        }
      }
    }
  };

  const onFilterReset = (e) => {
    e.preventDefault();
    const newFilterSelections = {
      ...filterSelections,
      engine: '',
      selectedKeyFeatures: [],
      hasSelectedFilter: false,
    };
    setFilterSelections(newFilterSelections);
    const { id } = getDefaultDriveObj();
    calcDriveChangeById(id, newFilterSelections);
  };

  const trimsHeader = translate(
    trimGroups.length === 1
      ? 'component.common.select.single.trim'
      : 'component.common.select.trim'
  );

  const getIsSelectionInSync = () => {
    //This variable accounts for cases where a trim with the 'display only' label
    //is accounting for a selected trim with opposing drive type and should be shown
    //in the filtered section
    const selectionHiddenByDisplayOnly =
      trimGroups
        .map((group) => {
          //if there is a group that accounts for the selected trim but has opposing drive
          //type and has the display only label we return true to show it's hidden. If we filter the
          //resulting array of any true values and the array is not empty we know that our selection's
          //accounted for. Also, 'isSelectionRevealed' is included in the conditional to ensure the
          //selected trim is actually selected by the user, to account for the default trim selection
          if (
            group.trimName === selections.trimName &&
            group.driveType.id !== selections.driveType &&
            group.displayOnlyLabel
          ) {
            return true;
          } else {
            return false;
          }
        })
        .filter((result) => result === true).length !== 0 &&
      isSelectionRevealed;

    return (
      !selectionHiddenByDisplayOnly &&
      (!isSelectionRevealed ||
        typeof flatMap(trimGroups, 'options').find((opt) => {
          return (
            opt.trimName === selections.trimName &&
            opt.transmissionCode === selections.transmission &&
            opt.engineCode === selections.engine
          );
        }) !== 'undefined')
    );
  };

  const isSelectionInSync = getIsSelectionInSync();

  const getIsFilterDefault = () => {
    if (bodyTypes && filterSelections) {
      const { rearWheelType, driveType } = getDefaultDriveObj();
      const isDriveDefault =
        rearWheelType === filterSelections.rearWheelType &&
        driveType === filterSelections.driveType;
      return (
        isDriveDefault &&
        !filterSelections.engine &&
        filterSelections.selectedKeyFeatures.length < 1
      );
    }
    return true;
  };

  const filteredTrimOptions = flatMap(
    trimGroupsFilteredByDT.filter((trim) => !trim.displayOnlyLabel),
    'options'
  );

  const availableEngineCodes = flatMap(filteredTrimOptions, 'engineCode');

  let filteredEngines = useMemo(() => {
    const availableIds = flatMap(
      trims.filter(
        (trim) =>
          hasCabSizes
            ? trim.cabSize === selections.cabSize
            : trim.bodyTypeId === selections.bodyTypeId,
        'options'
      )
    ).map((opt) => opt.styleId);
    return engines.filter((eng) => {
      for (const styleId of eng.styleIds) {
        if (availableIds.includes(styleId)) {
          return true;
        }
      }
    });
  }, []);

  filteredEngines = filteredEngines.map((engine) => {
    return {
      ...engine,
      disabled: !availableEngineCodes.includes(engine.id),
    };
  });

  const displayReset = !getIsFilterDefault();

  const analyticsKey = 'compare trims';

  const sortedKeyFeatures = [...filterSelections.selectedKeyFeatures].sort(
    (a, b) => {
      const aIndex = findIndex(keyFeatures, { code: a });
      const bIndex = findIndex(keyFeatures, { code: b });

      return aIndex - bIndex;
    }
  );

  const getSelectedTrim = () => {
    const selectedTrim = allPossibleTrims.find((trim) => {
      const driveType = get(trim, 'driveType.id');
      const rearWheelType = get(trim, 'rearWheelType');

      return (
        driveType === selections.driveType &&
        rearWheelType === selections.rearWheelType &&
        trim.trimName === selections.trimName
      );
    });

    return {
      ...selectedTrim,
      options: [
        selectedTrim.options.find(
          (opt) =>
            opt.engineCode === selections.engine &&
            opt.transmissionCode === selections.transmission
        ),
      ],
      compareAll: false,
    };
  };

  const selectedTrim = getSelectedTrim();

  const isFallbackToNoFilter =
    trimGroups.length === 1 && !filterSelections.hasSelectedFilter;

  return (
    <>
      {isFallbackToNoFilter ? (
        <TrimWithoutFiltering
          config={config}
          disclaimer={disclaimer}
          appResources={appResources}
          breakpoint={breakpoint}
          disableCompare={disableCompare}
          isSelectionRevealed={isSelectionRevealed}
          onChange={onChange}
          isUpdating={isUpdating}
          eatures={features}
          updatingOption={updatingOption}
          postalCode={postalCode}
          modelInfo={modelInfo}
          bottomOffset={bottomOffset}
          setBottomOffset={setBottomOffset}
          trims={trims}
          trimMatrix={trimMatrix}
        />
      ) : (
        <>
          <Routes>
            <Route
              path="/features/:styleId/*"
              element={
                <TrimFeaturesModal
                  appResources={appResources}
                  setBottomOffset={setBottomOffset}
                  features={features}
                  breakpoint={breakpoint}
                  disclaimer={disclaimer}
                  selections={selections}
                  disableCompare={disableCompare}
                  trimGroups={trimGroups}
                  isSelectionRevealed={isSelectionRevealed}
                  onClose={onClose}
                  onSelect={(option) => {
                    onChange(option);
                  }}
                  selectedKeyFeatureFilters={sortedKeyFeatures}
                  filteredGroups={[selectedTrim]}
                />
              }
            />
            <Route
              path={`/compare/*`}
              element={
                <TrimCompareModal
                  onClose={onClose}
                  onSelect={onChange}
                  config={config}
                  modelInfo={modelInfo}
                  postalCode={postalCode}
                  selections={selections}
                  trimGroups={trimGroups}
                  isSelectionRevealed={isSelectionRevealed}
                  hasCabSizes={hasCabSizes}
                  keyFeatureSelections={sortedKeyFeatures}
                />
              }
            />
            {/* TODO: Remove the corvette case once the deployment to production happens and enableVisualizer has correct vals */}
            <Route
              path="/features/:styleId/moreviews"
              element={<MoreViewsTwoD />}
            />
          </Routes>
          <GridView
            step={TRIMS}
            bottomOffset={isSelectionRevealed ? bottomOffset : '20px'}
            driveTypeFilter={
              <PopConFilter
                isAtLeastLarge={isAtLeastLarge}
                keyFeatures={keyFeatures}
                engines={filteredEngines}
                options={uniqueDriveAndWheelTypes}
                selections={filterSelections}
                onDriveChange={onDriveChange}
                onEngineChange={onEngineChange}
                filteredGroups={trimGroups}
                onFeatureChange={onFeatureChange}
                onFilterReset={onFilterReset}
                displayReset={displayReset}
                isUpdating={isUpdating}
                features={features}
                analyticsKey={analyticsKey}
              />
            }
            isSelectionRevealed={isSelectionRevealed}
            filterModifier="trims-pop-con-filters"
            options={
              <AlignCardsContent numberOfCards={numberOfCards}>
                {({ maxHeight, getRefByIndex }) => (
                  <>
                    {trimGroups.map((trim, index) => (
                      <TrimCard
                        enablePopConsTrimFilters={true}
                        trim={trim}
                        key={trim.styleId}
                        selections={selections}
                        disclaimer={disclaimer}
                        analyticsKey={trimsHeader}
                        msrp={totalMsrpWithOptions}
                        onChange={onChange}
                        isSelectionRevealed={isSelectionRevealed}
                        cardIndex={index}
                        displayOnlyLabel={trim.displayOnlyLabel}
                        isUpdating={isUpdating}
                        isUpdatingOption={
                          isUpdating &&
                          get(updatingOption, 'styleId') === trim.styleId
                        }
                        wait={index * 200}
                        animation={animation}
                        primaryRef={getRefByIndex(index)}
                        primaryDivHeight={maxHeight}
                      />
                    ))}
                  </>
                )}
              </AlignCardsContent>
            }
            filteredOutOption={
              !isSelectionInSync ? (
                <TrimCard
                  isFiltered={true}
                  enablePopConsTrimFilters={true}
                  readOnly={true}
                  trim={selectedTrim}
                  key={selectedTrim.trimName}
                  selections={selections}
                  disclaimer={disclaimer}
                  analyticsKey={trimsHeader}
                  msrp={totalMsrpWithOptions}
                  onChange={onChange}
                  isSelectionRevealed={isSelectionRevealed}
                  cardIndex={0}
                  displayOnlyLabel={selectedTrim.displayOnlyLabel}
                  isUpdating={isUpdating}
                  isUpdatingOption={
                    isUpdating &&
                    get(updatingOption, 'trimName') === selectedTrim.trimName
                  }
                  wait={0}
                  animation={animation}
                />
              ) : null
            }></GridView>
        </>
      )}
    </>
  );
};

export default TrimWithFiltering;
